import { PaymentElement } from '@stripe/react-stripe-js';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import React, { FC, Fragment, useId } from 'react';
import { useForm } from 'react-hook-form';
import { FORM_PAGES } from '../../Form/types';
import FormContainer from '../../Shared/FormContainer/FormContainer';
import { ErrorMessage, Line, TermsAndConditionsButton } from '../../Shared/Shared';
import { FORM_INFO_COMPONENT_FIELD_TYPES, FORM_INFO_COMPONENT_TYPES, FORM_INFO_FIELD_TYPES, FORM_INFO_SLOT_TYPES } from '../types';
import { AgreementText, Checkbox, CheckboxContainer, Description, DescriptionBox, FormInfoContainer, FormInfoWrapper, FormTitleContainer, PaymentDescription, PriceContainer, Title } from './styled';

import Checkmark from '../../../images/checkmark.svg';
import ModalDialog from '../../Modal/ModalDialog';
import TermsAndConditionsModal from '../../Modals/TermsAndConditionsModal';
import { FormTitle } from '../../Shared/FormContainer/styled';
import VoucherInput from './VoucherInput';

interface FormInfoSummaryProps {
  type: keyof typeof FORM_INFO_COMPONENT_TYPES;
  formOptions: Pick<ReturnType<typeof useForm>, 'handleSubmit'> & Partial<ReturnType<typeof useForm>>;
  onSubmit: (data: Record<string, string>) => void;
  scrollable: boolean;
  loading?: boolean;
  error?: string;
  containerError?: boolean;
  payment?: Record<string, any>;
  nextButtonSiblings?: React.ReactNode;
  selectedDefaultPaymentMethod?: string;
  previousPage?: {
    page?: FORM_PAGES;
    animation?: 'fade' | 'fade-out';
    onPrevious?: () => void;
    afterTransitionToPrev?: () => void;
  };
  showVoucherInput?: boolean;
  items: {
    replacedTitle?: string;
    type: FORM_INFO_FIELD_TYPES;
    description?: string | string[];
    descriptionsArray?: { crossed?: boolean; value: string; style?: string }[] | { crossed?: boolean; value: string; style?: string }[][];
    descriptionColumn?: boolean;
    lineAfter?: boolean;
  }[];
  bookingType?: string;
  nextButtonText?: string;
  paymentDescription?: string;
  terms_and_conditions_url?: string;
  branchName?: string;
  shouldGetCardAuth?: boolean;
  additionalInfo?: {
    terms_and_conditions?: { html: string; text: string };
    privacy_policy?: { html: string; text: string };
  };
}

const FormInfoSummary: FC<FormInfoSummaryProps> = ({
  type,
  formOptions,
  scrollable,
  onSubmit,
  loading = false,
  error = '',
  containerError = false,
  payment,
  nextButtonSiblings,
  selectedDefaultPaymentMethod,
  previousPage = { page: FORM_PAGES.BOOKING_SELECT_PETS },
  items,
  bookingType,
  nextButtonText,
  paymentDescription,
  showVoucherInput = false,
  terms_and_conditions_url,
  branchName,
  shouldGetCardAuth = true,
  additionalInfo
}) => {
  const { t } = useTranslation();
  const { registerAs } = FORM_INFO_COMPONENT_TYPES[type];
  const title = t(FORM_INFO_COMPONENT_TYPES[type].title);
  const { handleSubmit, register, formState, watch } = formOptions;

  const checkboxId = useId();
  const termsCheckboxId = useId();
  const tearmsAgreement = watch?.('termsAgreement');

  return (
    <FormInfoWrapper>
      <FormContainer
        onSubmit={handleSubmit(onSubmit)}
        accessibility={false}
        nextButtonText={nextButtonText || t('summary.payAndBook')}
        loading={loading}
        loadingData={loading}
        error={containerError || !tearmsAgreement}
        nextButtonSiblings={nextButtonSiblings}
        previousPage={previousPage}
        title={''}
        buttonMargin={'0'}
      >
        <FormInfoContainer marginBottom="0">
          <FormTitle>
            <Title>{title}</Title>
          </FormTitle>
          {items.map(({ replacedTitle, type, description, descriptionsArray, descriptionColumn, lineAfter }, i) => {
            const traslatedReplacedTitle = replacedTitle ? t(replacedTitle.toLowerCase()) : '';
            const title = t(FORM_INFO_COMPONENT_FIELD_TYPES[type].title);
            if (bookingType !== FORM_INFO_SLOT_TYPES.MULTI_DAY) {
              if (type === FORM_INFO_FIELD_TYPES.DATE_UNTIL) {
                return null;
              }
            }
            return (
              <Fragment key={i}>
                {title && (description || !!descriptionsArray?.length) && (
                  <FormTitleContainer>
                    {type === FORM_INFO_FIELD_TYPES.PRICE && <Line />}
                    <Title>{traslatedReplacedTitle || title}</Title>
                    {description && <Description>{description}</Description>}

                    <PriceContainer column={descriptionColumn}>
                      {!!descriptionsArray?.length &&
                        descriptionsArray?.map((item, index) => {
                          if (Array.isArray(item)) {
                            return (
                              <PriceContainer key={index}>
                                {item.map((itemVal, index) => {
                                  const { crossed, value, style } = itemVal;
                                  return (
                                    <>
                                      {value && (
                                        <Description crossed={crossed} key={index} style={style ? { fontWeight: style } : {}}>
                                          {value}
                                        </Description>
                                      )}
                                    </>
                                  );
                                })}
                              </PriceContainer>
                            );
                          }
                          const { crossed, value, style } = item;
                          return (
                            <>
                              {value && (
                                <Description crossed={crossed} key={index} style={style ? { fontWeight: style } : {}}>
                                  {value}
                                </Description>
                              )}
                            </>
                          );
                        })}
                    </PriceContainer>
                  </FormTitleContainer>
                )}
                {lineAfter && <Line />}
              </Fragment>
            );
          })}
          <Line />

          {showVoucherInput && (
            <>
              <VoucherInput />
              <Line />
            </>
          )}
          {shouldGetCardAuth && (
            <>
              <Title>{t('summary.paymentTitle')}</Title>
              {!!payment?.method && (
                <CheckboxContainer htmlFor={checkboxId}>
                  <input {...register!(registerAs)} type="checkbox" defaultChecked={true} id={checkboxId} />
                  <Checkbox>
                    <Checkmark />
                  </Checkbox>
                  <span>{t('summary.useCardOnFile')}</span>
                </CheckboxContainer>
              )}
              {(!selectedDefaultPaymentMethod || !payment?.method) && <PaymentElement />}
              {error && (!selectedDefaultPaymentMethod || !payment?.method) && <ErrorMessage>{error}</ErrorMessage>}
            </>
          )}
          <Line />
          <Title>{t('summary.termsTitle')}</Title>
          {paymentDescription && (
            <DescriptionBox>
              <PaymentDescription>{paymentDescription}</PaymentDescription>
            </DescriptionBox>
          )}

          <AgreementText>
            <CheckboxContainer htmlFor={termsCheckboxId} mobileWidth="40px" width="40px">
              <input {...register!('termsAgreement', { required: t('summary.errorAgreeToTerms'), validate: value => value })} type="checkbox" id={termsCheckboxId} defaultChecked={true} />
              <Checkbox checked={!!formState?.errors?.termsAgreement?.message}>
                <Checkmark />
              </Checkbox>
              {/* <Text> I agree to {branchName}'s</Text> */}
            </CheckboxContainer>
            {(additionalInfo?.privacy_policy?.html || additionalInfo?.terms_and_conditions?.html || !!terms_and_conditions_url) && (
              <TermsAndConditionsButton
                onClick={() =>
                  ModalDialog.openModal({
                    title: t('summary.termsModalTitle'),
                    content: () => <TermsAndConditionsModal additionalInfo={additionalInfo} termsAndConditionsUrl={terms_and_conditions_url} />
                  })
                }
              >
                {t('summary.agreeToTermsButton', { branchName })}
              </TermsAndConditionsButton>
            )}
            {!terms_and_conditions_url && !additionalInfo?.privacy_policy?.html && !additionalInfo?.terms_and_conditions?.html && (
              <div style={{ marginTop: '5px' }}> {t('summary.agreeToTermsText', { branchName })}</div>
            )}
          </AgreementText>
          <ErrorMessage noMargin>{formState?.errors?.termsAgreement?.message}</ErrorMessage>
        </FormInfoContainer>
      </FormContainer>
    </FormInfoWrapper>
  );
};

export default FormInfoSummary;
